import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { api } from '../../lib/api';
import { Modal } from '../../lib/Modal';
import { Banner, Button, Field, input } from '../../lib/ui';
import { t } from '../../lib/i18n';

/**
 * Reviewing what a scan proposed, one at a time.
 *
 * Osano shipped an inline-edit table for this, then needed a fullscreen escape hatch to
 * make it usable, then replaced the whole thing with a modal queue that advances to the
 * next item on save. That is a destination arrived at the hard way, so it is worth taking
 * directly.
 *
 * The advancing is the point. A list of forty suggestions is a chore nobody finishes; a
 * queue that hands you the next one is a chore that ends — and an unreviewed vendor is a
 * recipient the dialog does not name.
 */
export interface Suggestion {
  id: string;
  vendor_key: string;
  name: string;
  category_slug: string;
  policy_url: string;
  retention: string;
  cookies: string[];
  domains: string[];
}

// Translated in the render below, not here: a `t()` at module scope holds whichever
// language was current when the module was first imported.
const CATEGORIES: [string, string][] = [
  ['necessary', 'Notwendig — ohne das funktioniert die Seite nicht'],
  ['functional', 'Funktional — Komfort, Karten, Chat'],
  ['analytics', 'Statistik — Reichweite und Nutzung'],
  ['marketing', 'Marketing — Werbung und Retargeting'],
];

export function ReviewQueue({ items, base, onDone, onClose }: {
  items: Suggestion[];
  base: string;
  onDone: () => void;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [category, setCategory] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const current = items[index];
  // The list shrinks under us as decisions land, so "finished" is running off the end.
  const finished = !current;

  const decide = useMutation({
    mutationFn: async (decision: 'accept' | 'reject') => {
      if (decision === 'accept' && category && category !== current!.category_slug) {
        // Re-categorising before accepting, in one step: the category is the decision,
        // and making it a separate edit afterwards is how it gets skipped.
        await api(`${base}/vendors/${current!.id}`, {
          method: 'PATCH',
          body: {
            vendor_key: current!.vendor_key,
            name: current!.name,
            category_slug: category,
            policy_url: current!.policy_url,
            retention: current!.retention,
            cookies: current!.cookies,
            domains: current!.domains,
          },
        });
      }
      await api(`${base}/scan/suggestions/${current!.id}`, {
        method: 'POST',
        body: { decision },
      });
    },
    onSuccess: () => {
      setError(null);
      setCategory(null);
      setIndex((i) => i + 1);
      onDone();
    },
    onError: () => setError(t('Die Entscheidung konnte nicht gespeichert werden.')),
  });

  return (
    <Modal
      open
      wide
      title={finished ? t('Fertig') : t('Vorschlag {i} von {n}', { i: index + 1, n: items.length })}
      onClose={onClose}
      footer={
        finished ? (
          <Button onClick={onClose}>{t('Schließen')}</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>{t('Später')}</Button>
            <Button variant="danger" disabled={decide.isPending}
              onClick={() => decide.mutate('reject')}>
              {t('Verwerfen')}
            </Button>
            <Button disabled={decide.isPending} onClick={() => decide.mutate('accept')}>
              {decide.isPending ? t('Wird übernommen …') : t('Übernehmen')}
            </Button>
          </>
        )
      }
    >
      {finished ? (
        <p className="text-sm">
          {t('Alle Vorschläge sind entschieden. Übernommene Anbieter erscheinen im Auswahl-Dialog, sobald Sie das nächste Mal veröffentlichen.')}
        </p>
      ) : (
        <div className="space-y-4">
          {error && <Banner kind="error">{error}</Banner>}

          <div>
            <div className="text-[15px] font-semibold">{current.name}</div>
            <div className="mt-0.5 font-mono text-[11px] text-[var(--color-muted)]">
              {current.vendor_key}
            </div>
          </div>

          {(current.domains.length > 0 || current.cookies.length > 0) && (
            <div className="rounded-[3px] border border-[var(--color-line)] bg-[var(--color-paper)] p-3">
              <div className="eyebrow mb-1">{t('Gefunden als')}</div>
              <div className="font-mono text-[11px] leading-relaxed text-[var(--color-muted)]">
                {current.domains.map((d) => <div key={d}>{d}</div>)}
                {current.cookies.length > 0 && <div>{t('Cookies: {list}', { list: current.cookies.join(', ') })}</div>}
              </div>
            </div>
          )}

          <Field
            label={t('Kategorie')}
            hint={t('Die Einordnung entscheidet, ob dieser Anbieter vor einer Einwilligung geladen werden darf. „Notwendig“ heißt: immer erlaubt.')}
          >
            <select
              className={input}
              value={category ?? current.category_slug}
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORIES.map(([slug, label]) => (
                <option key={slug} value={slug}>{t(label)}</option>
              ))}
            </select>
          </Field>

          <p className="text-xs text-[var(--color-muted)]">
            {t('Übernehmen nimmt den Anbieter in die Liste auf, die im Auswahl-Dialog genannt wird. Verwerfen blendet ihn aus — er wird dann bei künftigen Scans nicht erneut vorgeschlagen.')}
          </p>
        </div>
      )}
    </Modal>
  );
}
